import * as Yup from "yup";
import { dateToyyyyMMdd } from "../../../../util/date";
import { validationMessages } from "../../../../constants/validation";
import { YupAdresseGeneric } from "../../../../util/yup";
import { AdresseDTO } from "../../../../interfaces/adresse";
import { getPatchOperations } from "../../../../util/form";
import { Adresse } from "../../../../interfaces/schema";
import { PersonKontaktAdresseDTO } from "../../../../interfaces/formDto";

export const kontanktadresseForm = (
  personId: string,
  kontaktadresseId?: string,
  beliggenhedsadresse?: Adresse,
): FormConfig<Form, PersonKontaktAdresseDTO> => {
  const query = `person/${personId}/kontaktadresser`;
  const queryWId = query + `/${kontaktadresseId}`;
  const requiredMsg = validationMessages.required;
  return {
    get: queryWId,
    title: "kontaktadresse",
    form: [
      {
        component: "boolean",
        id: "type",
        label: "I DAR?",
        initialValue: beliggenhedsadresse
          ? !!beliggenhedsadresse.darAdresseId
          : true,
      },
      {
        component: "adresse",
        id: "adresseDar",
        initialValue: undefined,
        label: "Adresse",
        isRequired: true,
        conditionalHidden: ({ values }) => !values.type,
      },
      {
        component: "andenAdresse",
        id: "andenAdresse",
        label: "Adresse",
        initialValue: beliggenhedsadresse
          ? beliggenhedsadresse
          : {
              adresselinie1: "",
              adresselinie2: "",
              postnummer: "",
              bynavn: "",
              adresselinie4: "",
              coNavn: "",
              landekode: "",
            },
        conditionalHidden: ({ values }) => values.type,
      },
      {
        component: "datepicker",
        id: "periodeFraDato",
        label: "Startdato",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeTilDatoInklusiv",
        label: "Slutdato",
      },
      {
        component: "textinput",
        id: "bemaerkning",
        initialValue: "",
        label: "Bemærkning",
      },
    ],
    validationSchema: Yup.object({
      type: Yup.boolean().required(requiredMsg),
      adresseDar: Yup.object().when("type", {
        is: true,
        then: (shcema) => shcema.required(requiredMsg),
      }),
      andenAdresse: Yup.object().when("type", {
        is: false,
        then: YupAdresseGeneric,
      }),
      periodeFraDato: Yup.date()
        .nullable()
        .required(requiredMsg)
        .test("", "Startdato er efter slutdato", function () {
          if (this.parent.periodeTilDatoInklusiv === undefined) return true;
          return (
            this.parent.periodeTilDatoInklusiv >= this.parent.periodeFraDato
          );
        }),
      periodeTilDatoInklusiv: Yup.date().nullable(),
    }),
    post: (values: Form) => {
      const { andenAdresse } = values;
      let body: PersonKontaktAdresseDTO;
      const globals = {
        bemaerkning: values.bemaerkning,
        periodeFraDato: dateToyyyyMMdd(values.periodeFraDato),
        periodeTilDatoInklusiv: values.periodeTilDatoInklusiv
          ? dateToyyyyMMdd(values.periodeTilDatoInklusiv)
          : "9999-12-31",
      };
      if (values.type) {
        body = {
          ...globals,
          adresse: values.adresseDar,
        };
      } else {
        body = {
          ...globals,
          adresse: {
            darAdresseId: null,
            ...andenAdresse,
            coNavn: andenAdresse.coNavn ?? null,
            adresselinie2: andenAdresse.adresselinie2 ?? null,
            adresselinie4: andenAdresse.adresselinie4 ?? null,
          },
        };
      }
      return {
        query,
        body,
      };
    },
    delete: () => {
      return {
        query: queryWId,
      };
    },
    patch: (values: Form) => {
      const body: PersonKontaktAdresseDTO = {} as PersonKontaktAdresseDTO;
      if (values.adresseDar) {
        body.adresse = values.adresseDar;
      } else if (values.andenAdresse) {
        body.adresse = { ...values.andenAdresse, darAdresseId: null };
      }
      if (values.periodeFraDato) {
        body.periodeFraDato = dateToyyyyMMdd(values.periodeFraDato);
      }
      if (values.periodeTilDatoInklusiv) {
        body.periodeTilDatoInklusiv = dateToyyyyMMdd(
          values.periodeTilDatoInklusiv,
        );
      } else if ("periodeTilDatoInklusiv" in values) {
        body.periodeTilDatoInklusiv = "9999-12-31";
      }
      if ("bemaerkning" in values) {
        body.bemaerkning = values.bemaerkning || "";
      }
      return {
        query: queryWId,
        body: JSON.stringify(getPatchOperations(body)),
      };
    },
  };
};

interface Form {
  type: boolean;
  adresseDar: AdresseDTO;
  andenAdresse: AdresseDTO;
  periodeFraDato: Date;
  periodeTilDatoInklusiv: Date;
  attention: string;
  bemaerkning: string;
}
